let cutIdCounter = 0

export default {
  namespaced: true,

  state: () => ({
    mode: null, // 'recording' or 'live'
    sourceId: null,
    sourceData: null,
    sourceUrl: null,
    startDate: null, // absolute start date for timeline labels
    hlsTimeOffset: 0, // seconds to add to HLS currentTime to get seconds since startDate
    earliestAvailable: 0, // seconds since startDate where content actually begins

    // Playback
    playing: false,
    currentTime: 0,
    duration: 0,
    volume: 0.5,
    muted: false,
    playerReady: false,
    isLive: true,

    // Timeline
    viewStart: 0,
    viewDuration: 60,

    // Selection
    markInTime: null,
    markOutTime: null,

    // Cuts
    cuts: [],
    editingCutIndex: null,

    // Commercial breaks parsed from HLS SCTE-35 / CUE-OUT DATERANGE tags.
    // Kept in seconds-since-`startDate` to line up with the timeline's time
    // domain. Upsert-by-id survives live segments ageing out of the DVR
    // window so breaks the user already scrubbed past stay marked.
    commercialBreaks: [],

    // Loop/Preview
    loopMode: null,
    previewMode: null,
    previewEnd: null,

    // Export
    exporting: false,
    exportTitle: '',
    sessionExports: []
  }),

  getters: {
    hasSelection: state => state.markInTime !== null && state.markOutTime !== null,
    viewEnd: state => state.viewStart + state.viewDuration,
    totalCutsDuration: state => state.cuts.reduce((sum, c) => sum + (c.outTime - c.inTime), 0)
  },

  mutations: {
    SET_MODE(state, { mode, sourceId }) { state.mode = mode; state.sourceId = sourceId },
    SET_SOURCE(state, { data, url }) { state.sourceData = data; state.sourceUrl = url },
    SET_START_DATE(state, date) { state.startDate = date ? new Date(date) : null },
    SET_HLS_TIME_OFFSET(state, val) { state.hlsTimeOffset = val },
    SET_EARLIEST_AVAILABLE(state, val) { state.earliestAvailable = val },
    SET_PLAYING(state, val) { state.playing = val },
    SET_CURRENT_TIME(state, val) { state.currentTime = val },
    SET_DURATION(state, val) {
      if (!isFinite(val) || val < 0) return
      const wasZero = state.duration === 0
      state.duration = val
      // Auto-fit view to full duration on first load for recordings
      if (wasZero && val > 0 && state.mode === 'recording') {
        state.viewStart = 0
        state.viewDuration = val
      }
    },
    SET_VOLUME(state, val) { state.volume = val },
    SET_MUTED(state, val) { state.muted = val },
    SET_PLAYER_READY(state, val) { state.playerReady = val },
    SET_IS_LIVE(state, val) { state.isLive = val },
    SET_VIEW(state, { start, duration }) {
      if (start !== undefined) state.viewStart = start
      if (duration !== undefined) state.viewDuration = duration
    },
    SET_MARK_IN(state, val) {
      state.markInTime = val
      if (state.editingCutIndex !== null && state.cuts[state.editingCutIndex]) {
        state.cuts[state.editingCutIndex].inTime = val
      }
    },
    SET_MARK_OUT(state, val) {
      state.markOutTime = val
      if (val !== null && state.editingCutIndex !== null && state.cuts[state.editingCutIndex]) {
        state.cuts[state.editingCutIndex].outTime = val
      }
    },
    CLEAR_SELECTION(state) { state.markInTime = null; state.markOutTime = null },
    ADD_CUT(state, cut) { state.cuts.push({ ...cut, id: ++cutIdCounter }) },
    REMOVE_CUT(state, index) { state.cuts.splice(index, 1) },
    UPDATE_CUT(state, { index, cut }) { state.cuts.splice(index, 1, { ...state.cuts[index], ...cut }) },
    CLEAR_CUTS(state) { state.cuts = [] },
    SET_EDITING_CUT(state, index) { state.editingCutIndex = index },
    UPSERT_COMMERCIAL_BREAK(state, brk) {
      const idx = state.commercialBreaks.findIndex(b => b.id === brk.id)
      if (idx >= 0) state.commercialBreaks.splice(idx, 1, brk)
      else state.commercialBreaks.push(brk)
    },
    CLEAR_COMMERCIAL_BREAKS(state) { state.commercialBreaks = [] },
    SET_LOOP_MODE(state, mode) { state.loopMode = mode },
    SET_PREVIEW_MODE(state, { mode, end }) { state.previewMode = mode; state.previewEnd = end },
    SET_EXPORTING(state, val) { state.exporting = val },
    SET_EXPORT_TITLE(state, val) { state.exportTitle = val },
    ADD_SESSION_EXPORT(state, exp) { state.sessionExports.push(exp) },
    UPDATE_SESSION_EXPORT(state, { id, data }) {
      const idx = state.sessionExports.findIndex(e => (e._id || e.media_id) === id)
      if (idx >= 0) Object.assign(state.sessionExports[idx], data)
    },
    REMOVE_SESSION_EXPORT(state, id) {
      state.sessionExports = state.sessionExports.filter(e => (e._id || e.media_id) !== id)
    },
    RESET(state) {
      state.mode = null; state.sourceId = null; state.sourceData = null; state.sourceUrl = null; state.startDate = null; state.hlsTimeOffset = 0; state.earliestAvailable = 0
      state.playing = false; state.currentTime = 0; state.duration = 0; state.playerReady = false
      state.viewStart = 0; state.viewDuration = 60
      state.markInTime = null; state.markOutTime = null
      state.cuts = []; state.editingCutIndex = null
      state.commercialBreaks = []
      state.loopMode = null; state.previewMode = null; state.previewEnd = null
      state.exporting = false; state.exportTitle = ''; state.sessionExports = []
    }
  },

  actions: {
    markIn({ commit, state }) {
      commit('SET_MARK_IN', state.currentTime)
      if (state.markOutTime !== null && state.markOutTime <= state.currentTime) {
        commit('SET_MARK_OUT', null)
      }
    },
    markOut({ commit, state }) {
      if (state.markInTime === null) return
      if (state.currentTime <= state.markInTime) return
      commit('SET_MARK_OUT', state.currentTime)
    },
    addCut({ commit, state }) {
      if (state.markInTime === null || state.markOutTime === null) return
      if (state.editingCutIndex !== null) {
        commit('UPDATE_CUT', { index: state.editingCutIndex, cut: { inTime: state.markInTime, outTime: state.markOutTime } })
        commit('SET_EDITING_CUT', null)
      } else {
        commit('ADD_CUT', { inTime: state.markInTime, outTime: state.markOutTime })
      }
      commit('CLEAR_SELECTION')
    }
  }
}
